import { useCallback, useEffect, useRef } from "react";
import {
  INSIGHT_ORG_BUNDLE_PRACTITIONER,
  insightAnalysisCacheKey,
} from "src-next/modules/clinical-insights/helpers/insightAnalysisCacheKey";
import type { LoadPhase } from "src-next/modules/clinical-insights/loadPhase";
import type {
  AnalysisCardId,
  ClinicalInsightDateRange,
} from "src-next/modules/clinical-insights/types/analysisTypes";
import { useCachedResource } from "src-next/shared/cache/useCachedResource";

interface UseCachedInsightPayloadArgs<T> {
  analysisId: AnalysisCardId | string;
  organizationId: string;
  dateRange?: ClinicalInsightDateRange;
  /**
   * Entra na chave. Busca FHIR da organização deve usar o segmento fixo
   * `INSIGHT_ORG_BUNDLE_PRACTITIONER` — o profissional continua filtrado no cliente.
   */
  practitionerFilter?: string;
  /** Card visitado: busca uma vez e relê a memória nas próximas vezes. */
  enabled?: boolean;
  fetcher: () => Promise<T>;
}

export function useCachedInsightPayload<T>({
  analysisId,
  organizationId,
  dateRange,
  practitionerFilter = INSIGHT_ORG_BUNDLE_PRACTITIONER,
  enabled = true,
  fetcher,
}: UseCachedInsightPayloadArgs<T>) {
  const cacheKey = insightAnalysisCacheKey({
    analysisId,
    organizationId,
    startDate: dateRange?.startDate,
    endDate: dateRange?.endDate,
    practitionerFilter,
  });

  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const stableFetcher = useCallback(() => fetcherRef.current(), []);

  return useCachedResource<T>({
    cacheKey,
    enabled: enabled && Boolean(cacheKey),
    fetcher: stableFetcher,
  });
}

export function deriveInsightLoadPhase(args: {
  enabled: boolean;
  isLoading: boolean;
  error: unknown;
  hasPayload: boolean;
  patientsLoading: boolean;
}): LoadPhase {
  if (!args.enabled) return "idle";
  if (!args.hasPayload && args.isLoading) return "records";
  if (!args.hasPayload && args.error) return "error";
  if (!args.hasPayload) return "records";
  if (args.patientsLoading) return "patients";
  return "done";
}

/** Propaga a fase só do card visível. Ocultos não marcam a página como ocupada. */
export function useReportInsightLoadPhase(
  isActive: boolean,
  phase: LoadPhase,
  onLoadPhaseChange?: (phase: LoadPhase) => void,
) {
  const onLoadPhaseChangeRef = useRef(onLoadPhaseChange);
  onLoadPhaseChangeRef.current = onLoadPhaseChange;

  useEffect(() => {
    if (!isActive) return;
    onLoadPhaseChangeRef.current?.(phase);
  }, [isActive, phase]);
}
